import { Editor } from "@tiptap/react";
import { Instance } from "tippy.js";
import { BrowserAgent } from "./BrowserAgent";

export const SAFARI_DEFAULT_SELECTION_BACKGROUND_COLOR = "#B4D5FE";

function getRangeSelectedNodes(range: Range) {
  let node: Node = range.startContainer;
  let endNode: Node = range.endContainer;

  // Special case for a range that is contained within a single node
  if (node == endNode) {
    if (node.nodeName === "A") return [node];
    else {
      return !node.childNodes.length
        ? [node]
        : [node.childNodes[range.startOffset]];
    }
  }

  if (node.nodeName === "#text") {
    if (
      node.textContent.length === range.startOffset ||
      range.startOffset === 0
    ) {
      while (!node.nextSibling) {
        node = node.parentNode;
      }
      return [node.nextSibling];
    }
  } else {
    return [node.childNodes[range.startOffset]];
  }
}

function getSelectedNodes() {
  if (window.getSelection) {
    let sel = window.getSelection();
    if (!sel.isCollapsed) {
      return getRangeSelectedNodes(sel.getRangeAt(0));
    }
  }
  return [];
}

function setCaret(el: HTMLElement, position?: boolean) {
  try {
    let range = document.createRange();
    let sel = window.getSelection();
    range.setStart(el, 1);
    range.collapse(position);
    sel.removeAllRanges();
    sel.addRange(range);
  } catch (error) {
    console.log(error);
  }
}

// God save this code, but... still needs to treat
// input different than text!
export const onCursorInsideAnchor = (e) => {
  const textCopy = document.createTextNode(e.target.textContent);
  const start = e.target?.previousSibling?.nodeName === "A";

  if (start) {
    setTimeout(() => {
      if (e.target?.parentNode?.parentNode) {
        e.target.parentNode.parentNode.insertBefore(
          textCopy,
          e.target.parentNode.nextSibling
        );
        e.target.remove();
        // @ts-ignore
        setCaret(textCopy, false);
      } else {
        e.target.remove();
      }
    }, 0);
  } else {
    if (e.target.nodeName === "A") return;

    e.target.remove();

    e.currentTarget.parentNode.insertBefore(textCopy, e.currentTarget);
    // @ts-ignore
    setCaret(textCopy, false);
  }
};

export function selectAnchorElement(
  anchor: HTMLAnchorElement,
  decorate?: boolean
) {
  if (!anchor) return;
  anchor.classList.remove("ProseMirror-selectednode");
  const htmlSelection = window.getSelection();
  const range = document.createRange();

  if (BrowserAgent.isWebKit()) {
    if (decorate) {
      range.selectNode(anchor);
    } else {
      range.selectNode(anchor);
    }
  } else {
    range.selectNodeContents(anchor); // NOT SAFARI
  }
  htmlSelection.removeAllRanges();
  htmlSelection.addRange(range);
}

export function showURLTooltip(
  tooltipId: string,
  tooltips: { [tooltipId: string]: Instance }
) {
  Object.entries(tooltips)?.forEach(([key, tip]) => {
    if (key !== tooltipId) tip?.hide?.();
  });
  tooltips?.[tooltipId]?.show();
}

export function getAnchorByURLAndSelect(tooltipId: string, decorate?: boolean) {
  const htmlNode = getAnchorElement(tooltipId);
  if (!htmlNode) return;
  selectAnchorElement(htmlNode, decorate);
}

export function selectAnchorElementURL(
  tooltipId: string,
  tooltips: { [tooltipId: string]: Instance }
) {
  getAnchorByURLAndSelect(tooltipId);
  showURLTooltip(tooltipId, tooltips);
  return true;
}

export function getAnchorElement(tooltipId: string): HTMLAnchorElement | null {
  let htmlNode = null;
  if (!tooltipId) return htmlNode;
  if (tooltipId.includes("/?")) {
    htmlNode = document.querySelector(`a[data-tooltip-id="${tooltipId}"]`);
    if (htmlNode) return htmlNode;
    htmlNode = document.querySelector(
      `a[data-tooltip-id="${tooltipId.replace("/?", "?")}"]`
    );
    if (htmlNode) return htmlNode;
  } else {
    htmlNode = document.querySelector(`a[data-tooltip-id="${tooltipId}"]`);
    if (htmlNode) return htmlNode;
    htmlNode = document.querySelector(
      `a[data-tooltip-id="${tooltipId.replace("?", "/?")}"]`
    );
    if (htmlNode) return htmlNode;
  }
  return htmlNode;
}

export const findAnchorNode = (editor: Editor, tooltipId: string) => {
  let nodePair = null;
  let descend = true;
  editor.state.doc.descendants((node, pos) => {
    if (!descend) return false;
    if (node.attrs.tooltipId === tooltipId) {
      nodePair = [pos, node];
      descend = false;
      return false;
    }
  });
  return nodePair;
};

export const getSelectedEditorText = (editor: Editor): string => {
  const { ranges } = editor.state.selection;
  const from = Math.min(...ranges.map((range) => range.$from.pos));
  const to = Math.max(...ranges.map((range) => range.$to.pos));

  const selectedText = editor.state.doc.textBetween(from, to).toString().trim();
  return selectedText;
};

// Save the selection
// Return a `Range` instance if there is a selected text
export const getSelectedRange = function (): null | Range {
  const selection = window.getSelection();
  return selection.rangeCount === 0 ? null : selection.getRangeAt(0);
};

export const isLinkRange = (range: Range): boolean => {
  if (!range) return false;
  return (
    range.startContainer === range.endContainer &&
    range.startContainer.nodeName === "A"
  );
};

const deepestAnchorChild = (element: HTMLElement | Node): Node | undefined => {
  if (!element) return;
  element =
    element.nodeType === 11
      ? (element as DocumentFragment).firstElementChild
      : (element as any);
  if (!element) return;
  if (element.nodeName === "A")
    return getAnchorElement(
      (element as HTMLAnchorElement).getAttribute("data-tooltip-id")
    );
  while (element.firstChild) {
    if (element.firstChild.nodeName === "A")
      return getAnchorElement(
        (element.firstChild as HTMLAnchorElement).getAttribute(
          "data-tooltip-id"
        )
      );
    element = element.firstChild;
  }
  return;
};

export const getSelectedAnchorElement = function (): HTMLAnchorElement | null {
  const range = getSelectedRange();

  // console.log("[PALHARI]", "range", range);

  if (!range) return null;
  const rounghSelection: Node[] = getSelectedNodes();

  // console.log("[PALHARI]", "rounghSelection", rounghSelection);

  if (rounghSelection?.length === 1) {
    if (rounghSelection[0]?.nodeName === "A") {
      return rounghSelection[0] as HTMLAnchorElement;
    }

    const deepRounghSelection = deepestAnchorChild(rounghSelection[0]);

    if (deepRounghSelection?.nodeName === "A") {
      return deepRounghSelection as HTMLAnchorElement;
    }
  }
  return null;
};

// Restore the selection
// `range` is a `Range` object
export const restoreRange = function (range: Range) {
  if (!range) return;
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
};
